import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {HTMLElement | null} */
  #previousActiveElement = null;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;
    // Remember where focus was (the trigger / add-to-cart button) so we can
    // restore it when the dialog closes.
    this.#previousActiveElement = /** @type {HTMLElement | null} */ (document.activeElement);

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      // Components flagged `data-awp-non-modal` open non-modally so third-party
      // modals (e.g. Giftnote's gift-message popup, rendered at <body> level)
      // can layer above them and stay interactive — dialog.showModal() puts the
      // dialog in the top layer and makes everything outside it `inert`, which a
      // body-level popup cannot escape cross-browser. The backdrop dim, click-
      // outside-to-close, Esc, and scroll-lock are all handled here / in CSS, so
      // non-modal keeps the same behavior minus the native focus trap.
      if (this.hasAttribute('data-awp-non-modal')) {
        dialog.show();
        // A non-modal dialog stays inside its DOM position — for the cart drawer
        // that's the sticky header's stacking context, which the sticky freeship
        // bar (higher z-index) would paint over. Flag the document so CSS lifts
        // the header context above it while open. (showModal()'s top layer did
        // this for free.)
        document.documentElement.setAttribute('data-awp-nonmodal-dialog', '');
        // Style hooks the cart drawer keys off INSTEAD of :has() selectors. A
        // :has() on the drawer/header ancestors makes iOS WebKit re-run :has
        // style-invalidation on every text mutation inside the drawer (cart
        // count, price, button label, the live-region announce…), and that path
        // segfaults the WebContent renderer. A plain attribute on the component
        // and a class on the header section avoid the :has() path entirely.
        this.setAttribute('data-awp-drawer-open', '');
        this.closest('.header-section')?.classList.add('awp_cart-header-lifted');
        // show() (unlike showModal()) does NOT move focus, so move it into the
        // dialog ourselves — focus shifts to the drawer on open for keyboard /
        // screen-reader users. (WCAG 2.2 §2.4.3 Focus Order)
        this.#moveFocusIntoDialog(dialog);
      } else {
        dialog.showModal();
      }
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Moves focus into the dialog (first focusable element, falling back to the
   * dialog itself). Used for non-modal dialogs, which — unlike showModal() — do
   * not move focus automatically.
   * @param {HTMLDialogElement} dialog
   */
  #moveFocusIntoDialog(dialog) {
    const focusable = dialog.querySelector(
      'a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const target = focusable instanceof HTMLElement ? focusable : dialog;
    if (target === dialog && !dialog.hasAttribute('tabindex')) {
      dialog.setAttribute('tabindex', '-1');
    }
    target.focus({ preventScroll: true });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');
    // Fade the manual backdrop out in step with the slide-out. The backdrop's
    // shown-state is keyed off the document-level data-awp-nonmodal-dialog flag
    // (see awp_cart-drawer.css.liquid — it survives the section morph that strips
    // data-awp-drawer-open), so drop THAT flag here at close START, not at the end
    // below, or the dim would linger a beat after the drawer has slid away.
    this.removeAttribute('data-awp-drawer-open');
    if (this.hasAttribute('data-awp-non-modal')) {
      document.documentElement.removeAttribute('data-awp-nonmodal-dialog');
    }

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (this.hasAttribute('data-awp-non-modal')) {
      // data-awp-nonmodal-dialog was already dropped at close start (above) so the
      // backdrop fades with the slide-out. The header z-index lift must persist for
      // the whole animation, so its class is removed here, at the end.
      this.closest('.header-section')?.classList.remove('awp_cart-header-lifted');
      // Return focus to whatever opened the dialog (modal dialogs do this
      // natively; non-modal show()/close() do not). (WCAG 2.2 §2.4.3)
      this.#previousActiveElement?.focus?.({ preventScroll: true });
    }
    this.#previousActiveElement = null;

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
